import { useState, useRef } from "react"
import dynamic from "next/dynamic"
import tw, { styled } from "twin.macro"

import API from "lib/api"
import LFLAPI from "lib/lfl"
import withSSRUser from "lib/withSSRUser"
import checkPageDataAuth from "lib/checkPageDataAuth"
import { MetaFields } from "components/Metadata"

import StoryBlock from "components/StoryBlock"
import Grid from "components/Grid"
import Column from "components/Column"

import { BodyDetail } from "styles/Text"
import Container from "components/Container"

const HeroStory = dynamic(import("components/Heroes/HeroStory"))

export const StoryHeader = styled(BodyDetail)`
  ${tw`inline-block text-dark-grey px-3 py-2 border border-solid border-dark-grey rounded-xl`}
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function StoryPage({
  pageData,
  storyData,
  nameData,
  featuredWords,
}) {
  const { Hero } = pageData
  const [activeAudio, setActiveAudio] = useState(null)
  const audioQueue = useRef(null)

  const allAudios = storyData?.sections?.map(({ audio }) => audio)

  // Stop all audio, remove references to audio and unset the active section
  function cleanupAudioQueue() {
    if (!audioQueue?.current) {
      return
    }
    setActiveAudio(null)
    audioQueue.current.forEach((audio) => audio.pause())
    audioQueue.current = null
  }

  // Create and launch audio queue from the section that was clicked
  function audioOnClick(idx) {
    cleanupAudioQueue()
    if (idx === activeAudio) {
      return
    }
    setActiveAudio(idx)
    const newAudioQueue = allAudios.slice(idx).map((src) => new Audio(src))
    newAudioQueue.forEach((audio, aIdx) => {
      if (aIdx === 0) {
        audio.addEventListener("canplaythrough", () => {
          audio.play()
        })
      } else {
        newAudioQueue[aIdx - 1].addEventListener("ended", () => {
          setActiveAudio(idx + aIdx)
          audio.play()
        })
      }

      if (aIdx + 1 === newAudioQueue.length) {
        audio.addEventListener("ended", () => {
          cleanupAudioQueue()
        })
      }
    })
    audioQueue.current = newAudioQueue
  }

  return (
    <>
      {Hero && (
        <HeroStory
          {...Hero}
          storyData={storyData}
          nameData={nameData}
          playStory={() => audioOnClick(0)}
        />
      )}
      <main tw="py-7.5 lg:py-40">
        <Container tw="hidden lg:block">
          <Grid halign="center">
            <Column size={{ lg: 8 / 12 }}>
              <Grid>
                <Column size={{ lg: 1 / 2 }}>
                  <StoryHeader as="h2">Bunuba Language</StoryHeader>
                </Column>
                <Column size={{ lg: 1 / 2 }}>
                  <StoryHeader as="h2">English Translation</StoryHeader>
                </Column>
              </Grid>
            </Column>
          </Grid>
        </Container>
        {storyData?.sections?.map((section, idx) => (
          <StoryBlock
            key={section.id}
            {...section}
            storyId={storyData.id}
            featuredWords={featuredWords}
            audioOnClick={() => audioOnClick(idx)}
            audioActive={idx === activeAudio}
          />
        ))}
      </main>
    </>
  )
}

export const getServerSideProps = withSSRUser(async ({ params }) => {
  // Redirect to named route when accessing by story ID
  if (
    !Number.isNaN(
      parseInt(Array.isArray(params.story) ? params.story[0] : params.story, 10)
    )
  ) {
    try {
      const { data } = await API.gql(`
      {
        storyPages(where: {LFLStoryID: "${params.story}"}) {
          UID
        }
      }
      `)
      if (data?.data?.storyPages?.[0]?.UID) {
        return {
          redirect: {
            destination: `/story/${data?.data?.storyPages?.[0]?.UID}`,
            permanent: true,
          },
        }
      }
    } catch (e) {
      console.log(`Didn't redirect story ${params.story} to named page`)
      console.log(e)
    }
  }
  const { HeroStoryQuery } = await import("components/Heroes/HeroStory")

  try {
    const { data } = await API.gql(
      `{
      storyPages
      (
        where: { UID: "${params.story}" }
      ) 
      {
        UID
        id
        LFLStoryID
        LFLNameID
        ${MetaFields}
        Hero ${HeroStoryQuery.fields}
      }
    }`
    )
    const authRedirect = checkPageDataAuth(data, `story/${params.story}`)
    if (authRedirect) {
      return authRedirect
    }
    const [{ data: theme }, { data: nav }] = await Promise.all([
      API.cachedGet("/theme", 60000),
      API.cachedGet("/navigation", 60000),
    ])

    const pageData = data?.data?.storyPages?.[0] || {}
    let storyData = null
    let featuredWords = []
    let nameData = {}

    if (pageData?.LFLStoryID) {
      try {
        storyData = await LFLAPI.story(pageData.LFLStoryID)
        const wordIds = storyData.featured_words.map(({ id }) => id)
        featuredWords = await Promise.all(wordIds.map((id) => LFLAPI.word(id)))
      } catch (e) {
        console.warn(`Couldn't fetch LFL Story for ${pageData.UID}`, e)
      }
    }
    if (pageData?.LFLNameID) {
      try {
        nameData = await LFLAPI.word(pageData?.LFLNameID)
      } catch (e) {
        console.warn(`Couldn't get story name data for ${pageData.UID}`, e)
      }
    }
    if (!pageData.UID) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        pageData,
        storyData,
        nameData,
        featuredWords,
        theme,
        nav,
      },
    }
  } catch (e) {
    console.warn(e)
    return {
      notFound: true,
    }
  }
})
